/**
 * MatchingPairs — memory-style card flip. Match prompt → correct answer.
 * Uses up to 6 questions per round (12-card grid).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import type { QuizGameSpec } from "@/types/quizGameSpec";
import type { GameRunResult } from "@/types/gameSpec";
import { GameHud } from "./hud/GameHud";
import { attemptsToRunResult, shuffle, type ArchetypeAttempt } from "./shared";

interface Props {
  spec: QuizGameSpec;
  onComplete: (r: GameRunResult) => void;
  onExit?: () => void;
}

interface Card {
  id: string;
  pairId: string;
  face: string;
  role: "prompt" | "answer";
  matched: boolean;
  flipped: boolean;
}

export const MatchingPairs = ({ spec, onComplete, onExit }: Props) => {
  const { theme, questions, tuning } = spec;
  const roundQs = useMemo(() => questions.slice(0, 6), [questions]);
  const total = roundQs.length;

  const initialCards = useMemo<Card[]>(() => shuffle(
    roundQs.flatMap((q, i) => [
      { id: `p-${i}`, pairId: `${i}`, face: q.prompt, role: "prompt" as const, matched: false, flipped: false },
      { id: `a-${i}`, pairId: `${i}`, face: q.correct, role: "answer" as const, matched: false, flipped: false },
    ]),
  ), [roundQs]);

  const [cards, setCards] = useState<Card[]>(initialCards);
  const [firstPick, setFirstPick] = useState<Card | null>(null);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(tuning.lives);
  const [timeLeft, setTimeLeft] = useState(tuning.timer_s);
  const [streak, setStreak] = useState(0);
  const [streakMax, setStreakMax] = useState(0);
  const [finished, setFinished] = useState(false);
  const attemptsRef = useRef<ArchetypeAttempt[]>([]);
  const startedAt = useRef(Date.now());
  const pickStartedAt = useRef(Date.now());
  const locked = useRef(false);

  useEffect(() => {
    if (finished) return;
    const id = window.setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { window.clearInterval(id); finish(false); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  function finish(won: boolean) {
    if (finished) return;
    setFinished(true);
    onComplete(attemptsToRunResult({
      archetype: "matching-pairs", attempts: attemptsRef.current, startedAt: startedAt.current,
      won: won && lives > 0, score, maxScore: total, streakMax,
    }));
  }

  function flip(card: Card) {
    if (locked.current || finished || card.matched || card.flipped) return;
    setCards((cs) => cs.map((c) => (c.id === card.id ? { ...c, flipped: true } : c)));

    if (!firstPick) {
      setFirstPick({ ...card, flipped: true });
      pickStartedAt.current = Date.now();
      return;
    }

    locked.current = true;
    const match = firstPick.pairId === card.pairId && firstPick.role !== card.role;
    const q = roundQs[Number(card.pairId)];
    attemptsRef.current.push({
      prompt: q.prompt, correct: q.correct, answered: card.face,
      isCorrect: match, timeMs: Date.now() - pickStartedAt.current,
    });

    setTimeout(() => {
      if (match) {
        setCards((cs) => cs.map((c) => (c.pairId === card.pairId ? { ...c, matched: true } : c)));
        setScore((s) => s + 1);
        setStreak((s) => { const n = s + 1; setStreakMax((m) => Math.max(m, n)); return n; });
        setFirstPick(null);
        locked.current = false;
        if (score + 1 >= total) finish(true);
      } else {
        setCards((cs) => cs.map((c) =>
          c.id === card.id || c.id === firstPick.id ? { ...c, flipped: false } : c,
        ));
        setLives((l) => { const n = l - 1; if (n <= 0) finish(false); return n; });
        setStreak(0);
        setFirstPick(null);
        locked.current = false;
      }
    }, 700);
  }

  const progress = Math.round((score / total) * 100);

  return (
    <div className="space-y-3">
      <GameHud title={theme.title} score={score} maxScore={total} lives={lives} timeLeft={timeLeft} progress={progress} streak={streak} />
      <div className="rounded-lg p-4" style={{ background: theme.palette.bg }}>
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
          {cards.map((c) => (
            <motion.button
              key={c.id}
              type="button"
              onClick={() => flip(c)}
              whileTap={{ scale: 0.96 }}
              disabled={c.matched}
              className="aspect-[4/3] rounded-lg p-2 text-sm font-medium flex items-center justify-center text-center transition-colors"
              style={{
                background: c.matched ? theme.palette.good : c.flipped ? theme.palette.surface : theme.palette.accent,
                color: "#fff",
                border: `2px solid ${theme.palette.accent}`,
                opacity: c.matched ? 0.6 : 1,
              }}
            >
              {c.flipped || c.matched ? c.face : "?"}
            </motion.button>
          ))}
        </div>
      </div>
      <div className="text-xs text-muted-foreground text-center">Flip two cards to find a matching pair.</div>
      {onExit && <div className="flex justify-end"><Button variant="ghost" size="sm" onClick={onExit}>Exit game</Button></div>}
    </div>
  );
};

export default MatchingPairs;
